import { ShoppingBag as CheckoutIcon } from '@mui/icons-material';
import { Card, CardContent, Typography, Box, Button, Alert } from '@mui/material';
import React from 'react';

import { CartCheckoutSectionProps, getCartOneTimeSubtotalCents, getCartRecurringSubtotalCents } from './types';

const CartCheckoutSection: React.FC<CartCheckoutSectionProps> = ({
  cart,
  onCheckout,
  canCheckout,
  getReadyItemsCount,
  getTotalItems
}) => {
  const recurringSubtotalCents = getCartRecurringSubtotalCents(cart.ready_items || []);
  const oneTimeSubtotalCents = getCartOneTimeSubtotalCents(cart.ready_items || []);

  return (
    <Card sx={{
      border: `2px solid var(--catalog-primary-color, #1976d2)`,
      bgcolor: 'var(--catalog-primary-color, #1976d2)',
      color: 'white'
    }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', textAlign: 'center' }}>
          Ready to Complete Your Order?
        </Typography>

        {/* Order Summary */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">Selected Services:</Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{getReadyItemsCount()}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">Total Items:</Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{getTotalItems()}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: oneTimeSubtotalCents > 0 ? 1 : 0 }}>
            <Typography variant="body2">Recurring subtotal:</Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              ${(recurringSubtotalCents / 100).toFixed(2)}/mo
            </Typography>
          </Box>
          {oneTimeSubtotalCents > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">One-time due today:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                ${(oneTimeSubtotalCents / 100).toFixed(2)}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Alerts */}
        {cart.pending_items.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2, bgcolor: 'rgba(255, 152, 0, 0.1)', color: 'white', border: '1px solid rgba(255, 152, 0, 0.3)' }}>
            <Typography variant="body2">
              <strong>Note:</strong> {cart.pending_items.length} item{cart.pending_items.length !== 1 ? 's' : ''} require verification. We'll contact providers for quotes.
            </Typography>
          </Alert>
        )}

        {/* Service Warnings */}
        {cart.ready_items?.some(item => (item as any).pricing_model === 'varying' || (item as any).has_varying_pricing) && (
          <Alert severity="info" sx={{ mb: 2, bgcolor: 'rgba(255, 255, 255, 0.1)', color: 'white', border: '1px solid rgba(255, 255, 255, 0.3)' }}>
            <Typography variant="body2">
              Some services have pricing that varies by configuration. Final pricing will be confirmed during checkout.
            </Typography>
          </Alert>
        )}

        {cart.ready_items?.some(item => (item as any).requires_address_verification) && (
          <Alert severity="warning" sx={{ mb: 2, bgcolor: 'rgba(255, 152, 0, 0.1)', color: 'white', border: '1px solid rgba(255, 152, 0, 0.3)' }}>
            <Typography variant="body2">
              Address verification required for some services. We'll verify during checkout.
            </Typography>
          </Alert>
        )}

        {/* Checkout Button */}
        <Button
          fullWidth
          variant="contained"
          size="large"
          startIcon={<CheckoutIcon />}
          onClick={onCheckout}
          disabled={!canCheckout}
          sx={{
            mb: 2,
            bgcolor: 'white',
            color: 'var(--catalog-primary-color, #1976d2)',
            fontWeight: 'bold',
            fontSize: '1.1rem',
            py: 1.5,
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.9)',
            },
            '&:disabled': {
              bgcolor: 'rgba(255, 255, 255, 0.5)',
              color: 'rgba(25, 118, 210, 0.5)'
            }
          }}
        >
          Complete Order - {getReadyItemsCount()} Item{getReadyItemsCount() !== 1 ? 's' : ''}
        </Button>

        {cart.pending_items.length > 0 && (
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', opacity: 0.8 }}>
            {cart.pending_items.length} item{cart.pending_items.length !== 1 ? 's' : ''} pending verification
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default CartCheckoutSection;